import {
    getSearchFilters,
    getSearchHotContents,
    getSearchRecommendations,
    getSearchResults
} from '@/app/search/api/searchApi';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import useAuth from '@/context/auth/useAuth';
import useStore from '@/services/store/useStore';

const SEARCH_RESULTS_PAGE_SIZE = 12;

export default function useSearch({
    searchPhrase,
    showRecommendations = false,
    showResults = false,
    searchRecommendationsQuery = '',
    searchResultsQuery = ''
} = {}) {
    const { babyMode } = useAuth();
    const { setRecentSearches } = useStore((state) => state);

    const { data: searchRecommendations, refetch: fetchRecommendations } = useQuery({
        queryKey: ['getSearchRecommendation', searchRecommendationsQuery],
        queryFn: () => getSearchRecommendations(searchRecommendationsQuery),
        staleTime: 30 * 60 * 1000,
        retry: false,
        enabled: showRecommendations && !!searchRecommendationsQuery
    });

    const { data: searchFilters } = useQuery({
        queryKey: ['getSearchFilters'],
        queryFn: () => getSearchFilters(babyMode),
        staleTime: 30 * 60 * 1000,
        retry: false
    });

    const { data: searchHotContents } = useQuery({
        queryKey: ['getSearchHotContents'],
        queryFn: getSearchHotContents,
        staleTime: 30 * 60 * 1000,
        retry: false
    });

    const {
        data: searchResults,
        fetchNextPage: searchResultsFetchNextPage,
        isFetchingNextPage: searchResultsIsFetchingNextPage,
        isLoading: searchResultsIsLoading
    } = useInfiniteQuery({
        queryKey: ['getSearchResults', searchResultsQuery],
        queryFn: async (context) => {
            try {
                const resp = await getSearchResults(
                    searchResultsQuery,
                    context.pageParam?.offset || 0,
                    SEARCH_RESULTS_PAGE_SIZE
                );
                setTimeout(() => {
                    setRecentSearches(searchPhrase);
                }, 0);
                return resp;
            } catch (error) {
                throw Error(error);
            }
        },
        getNextPageParam: (lastPage, _, lastPageParam) => {
            const someProblem = !lastPage;
            const totalCount = lastPage?.data?.total;
            const allDataAreLoaded =
                totalCount < SEARCH_RESULTS_PAGE_SIZE ||
                totalCount < lastPageParam?.offset + SEARCH_RESULTS_PAGE_SIZE ||
                someProblem;

            return allDataAreLoaded
                ? undefined
                : {
                      offset: lastPageParam
                          ? lastPageParam?.offset + SEARCH_RESULTS_PAGE_SIZE
                          : SEARCH_RESULTS_PAGE_SIZE
                  };
        },
        staleTime: 30 * 60 * 1000,
        retry: 2,
        enabled: showResults && !!searchResultsQuery
    });

    return {
        searchRecommendations,
        fetchRecommendations,
        searchFilters,
        searchHotContents,
        searchResults,
        searchResultsFetchNextPage,
        searchResultsIsFetchingNextPage,
        searchResultsIsLoading
    };
}
